import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { ClockIcon, SendIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import WarningBadge from '@repo/ui/warning-badge'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { diffuseTripBookings } from '../../../../../../api/centers/diffuse-trip-booking'
import { useObserver } from '../../../../../../lib/contexts/observer'
import useTranslate from '../../../../../../lib/hooks/use-translate'
import {
  canDiffuseTripBookings,
  CenterTripBookingDiffusionStatusEnum
} from '../../../../../../lib/types/enums/center/center-trip-status.enum'
import { DiffuseTripBookingsResponse } from '../../../../../../lib/types/models/center/center'
import { showAxiosError } from '../../../../../../lib/utils/functions/misc.functions'
import withCenter, { WithCenterProps } from '../../../../../hoc/with-center'
import OlCenterTabs from '../../../../ol-common/ol-center-trip-info/ol-center-tabs'
import { NotDiffusedCentersModal } from '../../not-diffused-centers-modal'

type Props = WithCenterProps & {
  id: number
}
const OlCenterTripsInfo = ({ id, center }: Props) => {
  // The backend owns the diffusion state; a never-diffused center must not look like a modified one.
  const status = center?.tripBookingDiffusionStatus

  const { t } = useTranslate()
  const diffuseObserver = useObserver({ key: 'diffuse-trip-booking' })
  const [notDiffusedCenters, setNotDiffusedCenters] = useState<
    DiffuseTripBookingsResponse['notDiffusedCenters']
  >([])

  const diffuseTripBookingsMutation = useMutation({
    mutationKey: ['diffuse-trip-bookings'],
    mutationFn: diffuseTripBookings,
    onError: showAxiosError,
    onSuccess: ({ diffusedCount, notDiffusedCenters }) => {
      diffuseObserver.notify(['ol-center-data'])
      if (diffusedCount > 0) toast.success(t('ol.center.trip.booking.success.message'))
      setNotDiffusedCenters(notDiffusedCenters)
    }
  })

  const diffusedBy = center?.tripBookingDiffusedBy

  return (
    <div className="mt-5">
      {status === CenterTripBookingDiffusionStatusEnum.MODIFIED_AFTER_DIFFUSION && (
        <WarningBadge text={t('ol.center.trips.info.warning.note')} />
      )}
      <div className="h-2" />
      <div className="flex items-center gap-2">
        <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
          {t('ol.center.trips.info.title')}
        </Typography>
        <InnerBox className="flex flex-row items-center gap-2 py-1 px-3">
          <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
            {t('ol.center.trips.info.date.of.diffusion')} :
          </Typography>
          <Badge
            text={valueRender(center?.tripBookingDiffusedAt, (date) =>
              DateFormatter.DDMMYYYY(date)
            )}
          />
          <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
            - {t('ol.center.trips.info.login.user')} :{' '}
            {diffusedBy ? toFullName(diffusedBy.firstName, diffusedBy.lastName) : '-'}
          </Typography>
        </InnerBox>
        <Badge
          color={tailwindColors.white.hover}
          borderColor={tailwindColors.grey.light.DEFAULT}
          icon={<ClockIcon />}
          iconPosition="start"
          text={t('ol.center.trips.info.time.note')}
        />
      </div>
      <div className="h-3" />
      {canDiffuseTripBookings(status) && (
        <Button
          label={t('ol.center.trips.info.diffuse.travel.info')}
          variant="ghost"
          icon={SendIcon}
          loading={diffuseTripBookingsMutation.isPending}
          onClick={() => diffuseTripBookingsMutation.mutate([id])}
        />
      )}

      <div className="h-3" />
      <OlCenterTabs id={id} mode="default" />

      <NotDiffusedCentersModal
        centers={notDiffusedCenters}
        onClose={() => setNotDiffusedCenters([])}
      />
    </div>
  )
}

export default withCenter(OlCenterTripsInfo)
